import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { BigNumber } from "ethers";
import { formatEther, parseEther } from "ethers/lib/utils";
import {
  useAccount,
  useContractRead,
  useContractWrite,
  useSwitchNetwork,
} from "wagmi";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Button as CButton,
  Spinner,
  Checkbox,
  useDisclosure,
  Flex,
  useColorMode,
} from "@chakra-ui/react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import _debounce from "lodash/debounce";

// Components
import Tooltip from "components/Tooltip";
import Alert from "components/Alert";
import Button from "./CustomButtom";

// Config
import { config, currentNetworkInfo } from "config/network";

// Constants
import { TXN_TYPES } from "constants/transactions";
import { VaultType } from "constants/vaults";
import {
  ENTER_WITHDRAW_AMOUNT,
  WITHDRAW_CTA,
  WITHDRAW_MAX_CTA,
} from "constants/analytics";

// Hooks
import useApp from "hooks/useApp";
import useChain from "hooks/useChain";
import useConverstionRates from "hooks/useConverstionRates";

// Utils
import {
  getMessage,
  isNumber,
  toFixedWithoutRounding,
  toStringPrecision,
} from "utils/common";

const Withdraw = ({ vault }: { vault: VaultType }) => {
  const { status, switchChain } = useChain();
  const { switchNetwork } = useSwitchNetwork();
  const { isConnected, address } = useAccount();

  const { isOpen: isChecked, onToggle } = useDisclosure();
  const { colorMode } = useColorMode();

  const { conversionToBnb } = useConverstionRates();

  const {
    openWalletModal,
    updateTransactionDetails,
    setTxnError,
    initiateNewTxn,
    track,
  } = useApp();

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isLoading: isWithdrawing, write: withdraw } = useContractWrite({
    mode: "recklesslyUnprepared",
    address: vault.address,
    abi: vault.abi,
    functionName: isChecked ? "withdraw" : "withdrawInBnbX",
    args: [
      parseEther(
        toStringPrecision(
          +toFixedWithoutRounding(
            amount && !Number.isNaN(+amount) ? amount : "0",
            8
          )
        )
      ),
    ],
    onError(error) {
      setTxnError(getMessage(error));
    },
    onSuccess(data) {
      updateTransactionDetails(
        data.hash,
        TXN_TYPES.WITHDRAW,
        amount,
        isChecked ? config.nativeCurrency.symbol : config.nativeCurrency.xsymbol
      );
    },
  });

  const { data: sharesInBnbx } = useContractRead({
    address: vault.address,
    abi: vault.abi,
    functionName: "getPortfolioValueInBnbX",
    chainId: +config.chainId,
    args: [address],
    enabled: !!address,
    structuralSharing: true,
  });

  const { data: swapRateResp } = useContractRead({
    address: vault.address,
    abi: vault.abi,
    functionName: "estimateBnbXToBnb",
    chainId: +config.chainId,
    args: [parseEther("1")],
    structuralSharing: true,
  });

  const {
    data: estimatedBnbx,
    refetch: estimatedBnbxRefetch,
    isLoading: isEstimating,
  } = useContractRead({
    address: vault.address,
    abi: vault.abi,
    functionName: "estimateBnbXWithdrawal",
    chainId: +config.chainId,
    args: [parseEther(amount || "0")],
    enabled: !!address && !!+amount && !Number.isNaN(+amount),
  });

  const estimate = useCallback(async () => {
    await estimatedBnbxRefetch();
  }, [estimatedBnbxRefetch]);

  const debounceFn = useRef(_debounce(estimate, 500)).current;

  useEffect(() => {
    if (!!+amount && !Number.isNaN(+amount)) {
      debounceFn();
    }
  }, [amount, debounceFn]);

  const withdrawalLimit = useMemo(
    () =>
      sharesInBnbx
        ? +toFixedWithoutRounding(formatEther(BigNumber.from(sharesInBnbx)), 8)
        : 0,
    [sharesInBnbx]
  );

  const withdrawalLimitInBnb = useMemo(
    () =>
      sharesInBnbx && conversionToBnb
        ? +toFixedWithoutRounding(
            toStringPrecision(
              parseFloat(formatEther(BigNumber.from(sharesInBnbx))) *
                conversionToBnb
            ),
            4
          )
        : 0,
    [sharesInBnbx, conversionToBnb]
  );

  const swapRate = useMemo(
    () =>
      swapRateResp
        ? +toFixedWithoutRounding(formatEther(swapRateResp.toString()), 4)
        : "",
    [swapRateResp]
  );

  const estimatedBnbxWithdrawAmount = useMemo(
    () =>
      estimatedBnbx
        ? toFixedWithoutRounding(
            formatEther((estimatedBnbx as any).amount.toString()),
            8
          )
        : "0",
    [estimatedBnbx]
  );

  const estimatedBnbxWithdrawFee = useMemo(
    () =>
      estimatedBnbx
        ? +(
            (+toFixedWithoutRounding(
              formatEther((estimatedBnbx as any).fee.toString()),
              12
            ) /
              +amount) *
            100
          ).toFixed(4)
        : 0,
    [estimatedBnbx, amount]
  );

  useEffect(() => {
    if (amount) {
      const error = checkInput(amount);
      setError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawalLimit, address]);

  useEffect(() => {
    if (amount && !isConnected) {
      setAmount("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  const checkInput = (value = amount.trim()) => {
    if (!isConnected) return "";

    let error = "";

    if (!+value || Number.isNaN(+value)) {
      error = "This field can't be blank or zero. Please provide valid input.";
    } else if (Number(value) > withdrawalLimit) {
      error = "Insufficient funds to execute the transaction.";
    }

    return error;
  };

  const handleChange = (value: string) => {
    const val = value.trim();

    const error = checkInput(val);

    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= 8) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }

    setError(error);
  };

  const handleMaxClick = () => {
    track(WITHDRAW_MAX_CTA, { unstake_amount_entered: withdrawalLimit });

    // const val = withdrawalLimit - stakingFees;

    handleChange(
      toFixedWithoutRounding(withdrawalLimit.toString(), 8).toString()
    );
  };

  const handleWithdraw = () => {
    const error = checkInput();
    if (error) {
      setError(error);
    } else {
      initiateNewTxn(false);
      track(WITHDRAW_CTA, {
        unstake_amount_entered: amount,
      });
      withdraw?.();
      setAmount("");
    }
  };

  const estimatedBnbWithdrawAmount = useMemo(
    () =>
      swapRate
        ? toFixedWithoutRounding(
            (+estimatedBnbxWithdrawAmount * +swapRate).toString(),
            8
          )
        : "0",
    [swapRate, estimatedBnbxWithdrawAmount]
  );

  const displayValueBnbx = useMemo(
    () =>
      withdrawalLimit
        ? +toFixedWithoutRounding(withdrawalLimit.toString(), 4) < 0.0001
          ? "< 0.0001"
          : +toFixedWithoutRounding(withdrawalLimit.toString(), 4)
        : "0",
    [withdrawalLimit]
  );

  const displayValueBnb = useMemo(
    () =>
      withdrawalLimitInBnb
        ? +toFixedWithoutRounding(withdrawalLimitInBnb.toString(), 4) < 0.0001
          ? "< 0.0001"
          : +toFixedWithoutRounding(withdrawalLimitInBnb.toString(), 4)
        : "0",
    [withdrawalLimitInBnb]
  );

  const displayEstimatedBnbWithdrawAmount = useMemo(
    () =>
      +amount && estimatedBnbWithdrawAmount
        ? +toFixedWithoutRounding(estimatedBnbWithdrawAmount.toString(), 4) <
          0.0001
          ? "< 0.0001"
          : +toFixedWithoutRounding(estimatedBnbWithdrawAmount.toString(), 4)
        : "0",
    [amount, estimatedBnbWithdrawAmount]
  );

  const displayEstimatedBnbxWithdrawAmount = useMemo(
    () =>
      +amount && estimatedBnbxWithdrawAmount
        ? +toFixedWithoutRounding(estimatedBnbxWithdrawAmount.toString(), 4) <
          0.0001
          ? "< 0.0001"
          : +toFixedWithoutRounding(estimatedBnbxWithdrawAmount.toString(), 4)
        : "0",
    [amount, estimatedBnbxWithdrawAmount]
  );

  return (
    <Box mt="1rem">
      <Text fontSize={{ base: 12, lg: 16 }}>
        My Current Value :
        <Text as="span" mx="4px" fontFamily="semi-bold">
          {displayValueBnbx}
        </Text>
        BNBx ({displayValueBnb} BNB)
      </Text>
      <InputGroup size="lg" mt="1.5rem" mb="1rem">
        <InputLeftElement pointerEvents="none">
          <Image
            src={currentNetworkInfo.tokenXIcon[colorMode].src}
            width={28}
            height={28}
            alt="token-icon"
          />
        </InputLeftElement>
        <Input
          type="text"
          borderWidth="2px"
          borderRadius="0.5rem"
          isInvalid={!!error}
          borderColor={error ? "crimson" : "brand.primary"}
          placeholder="Enter BNBx Amount"
          value={amount}
          fontSize={{ base: 14, lg: 16 }}
          onChange={(e) => {
            track(ENTER_WITHDRAW_AMOUNT, {
              amount: e.target.value,
            });
            handleChange(e.target.value);
          }}
          _focus={{
            boxShadow: "none",
            outline: "none",
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
          }}
          _hover={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
            _disabled: {
              borderWidth: "1px",
            },
          }}
        />
        <InputRightElement width="4.5rem">
          <CButton
            bg="brand.backgroundLight"
            h="1.75rem"
            size="sm"
            p={1}
            onClick={handleMaxClick}
            _dark={{
              color: "brand.textColor",
            }}
            _hover={{
              bg: "brand.primary",
              color: "white",
              outline: "none",
              borderColor: "brand.primary",
            }}
            _focus={{
              outline: "none",
            }}
          >
            Max
          </CButton>
        </InputRightElement>
      </InputGroup>
      {error && (
        <Alert type="danger" withIcon={true} mb="1rem">
          <Text
            fontSize={{ base: 12, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            {error}
          </Text>
        </Alert>
      )}
      {!vault.isPaused && (
        <Box py="0.5rem" marginBottom="1rem" borderRadius="0.5rem">
          <Checkbox
            onChange={onToggle}
            isChecked={isChecked}
            borderColor="brand.textColor"
          >
            <Flex gap="0.5rem" ml="0.5rem">
              <Image
                src={currentNetworkInfo.icon[colorMode].src}
                width={20}
                height={20}
                alt="token-icon"
              />
              <Text fontSize={{ base: 12, lg: 14 }}>Withdraw in BNB</Text>
            </Flex>
          </Checkbox>
        </Box>
      )}
      {isChecked && (
        <Alert type="warning" withIcon={true}>
          <Text
            fontSize={{ base: 10, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            BNBx will be withdrawn from wombat LP and swapped to get BNB.
            <br /> The current swap rate is 1 BNBx : {swapRate || "-"} BNB.
          </Text>
        </Alert>
      )}
      <Flex my="1rem" fontSize={{ base: 12, lg: 16 }} gap="0.5rem">
        You will get :{" "}
        <Text as="span" fontFamily="semi-bold">
          {isChecked ? (
            `${displayEstimatedBnbWithdrawAmount} BNB`
          ) : isEstimating ? (
            <Spinner color="brand.primary" size="sm" />
          ) : (
            <Flex align="center" gap="0.5rem">
              <Text>{displayEstimatedBnbxWithdrawAmount || "0"} BNBx</Text>
              {!!estimatedBnbxWithdrawFee && (
                <Tooltip
                  closeDelay={2000}
                  placement="right"
                  label={
                    <Text pointerEvents="all">
                      Because of the extreme coverage ratios, funds will be
                      withdrawn at a significant discount.
                      <Text
                        ml="2px"
                        as="a"
                        href={vault.feeKnowMore}
                        target="_blank"
                        rel="noreferrer"
                        color="brand.primary"
                      >
                        Know More
                      </Text>
                    </Text>
                  }
                >
                  <Text ml="4px" color="brand.textInactive" fontSize="14px">
                    <AiOutlineInfoCircle />
                  </Text>
                </Tooltip>
              )}
            </Flex>
          )}
        </Text>
      </Flex>
      {!error && (
        <Alert
          type="info"
          withIcon={true}
          display={isConnected ? "none" : "flex"}
        >
          <Text
            fontSize={{ base: 12, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            Connect your wallet to start withdrawing.
          </Text>
        </Alert>
      )}

      <Button
        gap={0}
        onClick={
          error
            ? undefined
            : isConnected
            ? !status
              ? !switchNetwork
                ? undefined
                : switchChain
              : handleWithdraw
            : () => {
                openWalletModal({
                  location: "portfolio_deposit_tab",
                  vault: vault.name,
                });
              }
        }
        disabled={isConnected ? isWithdrawing || !!error : false}
        mt="2rem"
        size={{ base: "sm", lg: "lg" }}
      >
        {isWithdrawing ? (
          <Spinner size="md" color="brand.textLight" />
        ) : (
          <Text>
            {isConnected
              ? !status
                ? !switchNetwork
                  ? "Switch Network in the Wallet"
                  : "Switch Network"
                : "Withdraw"
              : "Connect wallet"}
          </Text>
        )}
      </Button>
    </Box>
  );
};

export default Withdraw;
